import { serviceOrigin } from './services';
import { instanceReady } from '../hooks/useInstanceMode';
// Server-side user-preferences sync (Alex 2026-06-22, contract w/ Cloud).
//
// auth-api persists a free-form prefs blob keyed by the canonical users.id
// (derived server-side from the JWT — we send no key), so a user's settings
// follow them across devices/browsers regardless of which email alias they
// logged in with.
//
//   GET   /api/v1/user/preferences        → { prefs: {...} }
//   PATCH /api/v1/user/preferences  {k:v}  → shallow-merge top-level keys,
//                                            returns { prefs: <merged> }
//
// Strategy: localStorage stays the instant/offline source of truth. We
// MONKEYPATCH localStorage.setItem so any write to a synced key debounce-
// PATCHes the server — no need to thread sync calls through every setter.
// On startup we pull the server blob, seed localStorage, and re-apply the
// live-applied prefs (accent/success/theme).

import { initTheme } from './accent';

// User-prefs sync lives on CLOUD-API, not auth-api (AuthAPI verified 2026-06-24:
// the route never existed in auth-api; cloud-api/cloud2 serve it). Relative +
// same-origin so it routes through the FE's /api proxy to cloud-api on every
// host (cloud2.arkturian, cloud2.mac, arkserver) — no hardcoded host.
// ABSOLUT gegen auth-api, nicht relativ. Relativ landete der Aufruf auf dem
// Ursprung der Oberfläche (`cloud2.mac.arkturian.com/api/v1/user/preferences`)
// und damit auf einem 404 — gemeldet von Alex am 12.08.
//
// Die Route lebt bei auth-api und ist seit Clouds #1028 dort angeschlossen;
// geprüft: sie antwortet mit 401 statt 404, existiert also. Denselben
// Ursprung spricht der Client bereits für die Token-Erneuerung an, die
// CORS-Freigabe ist damit belegt.
const prefsUrl = () => { const o = serviceOrigin('auth'); return o ? `${o}/api/v1/user/preferences` : null; };
const AUTH_STORAGE_KEY = 'ark-auth';

// localStorage keys that are identity-bound (sync) vs device-local (skip).
// Values are stored verbatim as their localStorage string — JSON-encoded
// arrays (hidden-nodes/owners) round-trip as strings, which is fine.
const SYNC_KEYS = new Set([
  'cloud-accent-color',
  'cloud-success-color',
  'cloud-theme',
  'cloud-v2-hidden-nodes',
  'cloud-v2-hidden-owners',
  'cloud-tts-mode',
  'cloud-auto-tts',
  'cloud-voice-autosend',
  'cloud-voice-ambient',
  'cloud-group-by',
  'cloud-sort-by',
  'cloud-v2-settings',
  'cloud-multi-workspaces', // Multi-Agent workspaces follow the user across logins (Alex 2026-06-26)
]);

function bearer(): string | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw)?.access_token || null;
  } catch { return null; }
}

// Debounced PATCH — collect changed keys, flush as one shallow-merge body.
let pending: Record<string, string> = {};
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let suppress = false; // true while pullPrefs writes localStorage (no echo)

function flush(): void {
  flushTimer = null;
  const token = bearer();
  const body = pending;
  pending = {};
  if (!token || Object.keys(body).length === 0) return;
  void instanceReady().then(() => {
    const url = prefsUrl();
    if (!url) return;
    return fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    }).catch(() => { /* offline — localStorage already holds the value, retried on next change */ });
  });
}

function queuePush(key: string): void {
  if (suppress || !SYNC_KEYS.has(key)) return;
  pending[key] = localStorage.getItem(key) ?? '';
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flush, 700);
}

/** Pull the server blob → seed localStorage → re-apply live prefs. */
export async function pullPrefs(): Promise<void> {
  const token = bearer();
  if (!token) return;
  await instanceReady(); // Boot-Barriere: nie gegen den Betreiber-Default racen
  // Kein auth-Dienst auf dieser Instanz → kein Prefs-Sync (localStorage bleibt
  // die Wahrheit). Spart ein vorhersehbares 404 in der Kunden-Konsole.
  if (!prefsUrl()) return;
  const url = prefsUrl();
  if (!url) return;
  let prefs: Record<string, unknown> | null = null;
  try {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return;
    prefs = (await res.json())?.prefs ?? null;
  } catch { return; }
  if (!prefs || typeof prefs !== 'object') return;

  suppress = true;
  let changed = false;
  for (const key of SYNC_KEYS) {
    const v = prefs[key];
    if (v == null) continue;
    const str = typeof v === 'string' ? v : JSON.stringify(v);
    if (localStorage.getItem(key) !== str) {
      localStorage.setItem(key, str);
      changed = true;
    }
  }
  suppress = false;

  if (changed) {
    // Live-apply the visual prefs immediately; the rest is read by their
    // components on (re)mount. Notify listeners that want to re-read now.
    initTheme();
    window.dispatchEvent(new CustomEvent('cloud-prefs-pulled'));
  }
}

/** Install the localStorage.setItem interceptor (once, at startup). */
export function installPrefsSync(): void {
  const ls = window.localStorage;
  const orig = ls.setItem.bind(ls);
  ls.setItem = (key: string, value: string) => {
    orig(key, value);
    queuePush(key);
  };
}
